<template>
  <div class="max-w-7xl w-full mx-auto space-y-4">
    <div>
      <h1 class="text-2xl font-bold text-gray-800">Clip Explorer</h1>
      <p class="text-sm text-gray-600">Search and browse clips for your selected university scope.</p>
    </div>

    <div class="border rounded-lg bg-white p-4">
      <div class="grid grid-cols-1 md:grid-cols-6 gap-3">
        <div>
          <label class="block text-xs text-gray-600 mb-1">University</label>
          <select v-model="selectedSlug" class="border rounded px-3 py-2 w-full" @change="onUniversityChange">
            <option v-for="u in allowedUniversities" :key="u.slug" :value="u.slug">{{ u.name }} ({{ u.slug }})</option>
          </select>
        </div>
        <div>
          <label class="block text-xs text-gray-600 mb-1">Season</label>
          <select v-model="selectedSeasonKey" class="border rounded px-3 py-2 w-full" @change="onSeasonChange">
            <option value="all">All ceremonies</option>
            <option v-for="s in seasonOptions" :key="s.key" :value="s.key">{{ s.label }}</option>
          </select>
        </div>
        <div>
          <label class="block text-xs text-gray-600 mb-1">Ceremony</label>
          <select v-model="selectedCeremonyId" class="border rounded px-3 py-2 w-full" @change="refresh">
            <option value="">All ceremonies</option>
            <option v-for="c in ceremoniesForSeason" :key="c.id" :value="c.id">{{ formatCeremony(c) }}</option>
          </select>
        </div>
        <div>
          <label class="block text-xs text-gray-600 mb-1">Status</label>
          <select v-model="statusFilter" class="border rounded px-3 py-2 w-full" @change="refresh">
            <option value="ready">Ready (Generated / Notified)</option>
            <option value="all">All statuses</option>
          </select>
        </div>
        <div class="md:col-span-2">
          <label class="block text-xs text-gray-600 mb-1">Graduate name</label>
          <input
            v-model="searchQuery"
            type="text"
            class="border rounded px-3 py-2 w-full"
            placeholder="Search by graduate name"
            @input="onSearchInput"
          />
        </div>
      </div>

      <div class="mt-3 flex items-center gap-2">
        <button
          type="button"
          class="px-3 py-2 rounded text-xs border bg-white hover:bg-gray-50"
          :disabled="loading"
          @click="refresh"
        >
          Refresh
        </button>
        <span v-if="loading" class="text-xs text-gray-500">Loading clips...</span>
      </div>
    </div>

    <div v-if="error" class="text-sm text-red-600">{{ error }}</div>

    <div class="border rounded-lg bg-white overflow-hidden">
      <table class="min-w-full text-sm">
        <thead class="bg-gray-50 text-gray-700">
          <tr>
            <th class="text-left px-3 py-2">Graduate</th>
            <th class="text-left px-3 py-2">Ceremony Date/Time</th>
            <th class="text-left px-3 py-2">Status</th>
            <th class="text-left px-3 py-2">Submitted</th>
            <th v-if="showClipMetrics" class="text-left px-3 py-2">Views</th>
            <th v-if="showClipMetrics" class="text-left px-3 py-2">Shares</th>
            <th class="text-left px-3 py-2">Clip</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!loading && !items.length">
            <td :colspan="showClipMetrics ? 7 : 5" class="px-3 py-6 text-center text-gray-500">No clips found for this scope.</td>
          </tr>
          <tr v-for="row in items" :key="row.submission_id" class="border-t">
            <td class="px-3 py-2">{{ row.registration_name || 'Unknown graduate' }}</td>
            <td class="px-3 py-2">{{ formatDateTime(row.ceremony_datetime) }}</td>
            <td class="px-3 py-2">{{ row.submission_status || 'Unknown' }}</td>
            <td class="px-3 py-2">{{ formatDateTime(row.submitted_at) }}</td>
            <td v-if="showClipMetrics" class="px-3 py-2">{{ clipMetric(row, 'total_views') }}</td>
            <td v-if="showClipMetrics" class="px-3 py-2">{{ clipMetric(row, 'total_shares') }}</td>
            <td class="px-3 py-2">
              <a :href="row.clip_path" target="_blank" rel="noopener noreferrer" class="text-blue-700 hover:underline">Open clip</a>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="flex items-center justify-center">
      <button
        v-if="hasMore && !loadingMore"
        type="button"
        class="px-3 py-2 rounded text-xs border bg-white hover:bg-gray-50"
        @click="loadMore"
      >
        Load more
      </button>
      <span v-if="loadingMore" class="text-xs text-gray-500">Loading more...</span>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Auth from '../../api/auth.js'

export default {
  name: 'ClipsExplorer',
  data() {
    const payload = Auth.getPayload()
    const tokenUniversities = Array.isArray(payload?.universities) ? payload.universities : []
    const roles = Array.isArray(payload?.roles) ? payload.roles : []
    return {
      tokenUniversities,
      roles,
      allowedUniversities: [],
      selectedSlug: tokenUniversities[0] || '',
      ceremonies: [],
      seasonOptions: [],
      selectedSeasonKey: 'all',
      selectedCeremonyId: '',
      statusFilter: 'ready',
      searchQuery: '',
      searchDebounceTimer: null,
      items: [],
      clipMetricsBySubmission: {},
      clipMetricsVisible: true,
      nextCursor: null,
      hasMore: false,
      loading: false,
      loadingMore: false,
      error: '',
      latestRequestId: 0,
    }
  },
  created() {
    this.initialLoad()
  },
  beforeDestroy() {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
  },
  computed: {
    selectedUniversity() {
      return (this.allowedUniversities || []).find(u => u.slug === this.selectedSlug) || null
    },
    metricsAllowedByRole() {
      const set = new Set(this.roles || [])
      return set.has('admin') || set.has('analytics-viewer') || set.has('analytics_viewer')
    },
    showClipMetrics() {
      return this.metricsAllowedByRole && this.clipMetricsVisible
    },
    ceremoniesForSeason() {
      if (this.selectedSeasonKey === 'all') return this.ceremonies || []
      const season = (this.seasonOptions || []).find(s => s.key === this.selectedSeasonKey)
      if (!season) return this.ceremonies || []
      const allowed = new Set(season.ceremonyIds || [])
      return (this.ceremonies || []).filter(c => allowed.has(String(c.id)))
    },
  },
  methods: {
    async initialLoad() {
      await this.fetchUniversities()
      await this.onUniversityChange()
    },
    async fetchUniversities() {
      try {
        const r = await axios.get('/universities')
        const rows = Array.isArray(r.data) ? r.data : []
        const allowed = new Set(this.tokenUniversities)
        this.allowedUniversities = rows.filter(u => allowed.has(u.slug))
      } catch (_) {
        this.allowedUniversities = (this.tokenUniversities || []).map(slug => ({ slug, name: slug }))
      }
      if (!this.selectedSlug && this.allowedUniversities.length) {
        this.selectedSlug = this.allowedUniversities[0].slug
      }
    },
    async onUniversityChange() {
      this.selectedCeremonyId = ''
      this.selectedSeasonKey = 'all'
      this.items = []
      this.clipMetricsBySubmission = {}
      this.clipMetricsVisible = true
      this.nextCursor = null
      this.hasMore = false
      await this.fetchCeremonies()
      await this.refresh()
    },
    async fetchCeremonies() {
      this.ceremonies = []
      const uni = this.selectedUniversity
      if (!uni || !uni.id) return
      try {
        const r = await axios.get(`/universities/${uni.id}`)
        const rows = Array.isArray(r?.data?.ceremonies) ? r.data.ceremonies : []
        this.ceremonies = rows
        this.seasonOptions = this.buildSeasonOptions(rows)
      } catch (_) {
        this.ceremonies = []
        this.seasonOptions = []
      }
    },
    onSeasonChange() {
      if (this.selectedCeremonyId) {
        const hasSelected = this.ceremoniesForSeason.some(c => String(c.id) === String(this.selectedCeremonyId))
        if (!hasSelected) this.selectedCeremonyId = ''
      }
      this.refresh()
    },
    onSearchInput() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = setTimeout(() => this.refresh(), 250)
    },
    buildSeasonOptions(ceremonies) {
      const sorted = [...(ceremonies || [])]
        .filter(c => c && c.datetime)
        .sort((a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime())
      if (!sorted.length) return []
      const gapMs = 14 * 24 * 60 * 60 * 1000
      const groups = []
      let current = []
      for (const ceremony of sorted) {
        if (!current.length) {
          current.push(ceremony)
          continue
        }
        const prev = current[current.length - 1]
        const prevTs = new Date(prev.datetime).getTime()
        const currTs = new Date(ceremony.datetime).getTime()
        if (!Number.isFinite(prevTs) || !Number.isFinite(currTs) || (currTs - prevTs) > gapMs) {
          groups.push(current)
          current = [ceremony]
        } else {
          current.push(ceremony)
        }
      }
      if (current.length) groups.push(current)
      return groups.map((group, idx) => {
        const first = group[0]
        const last = group[group.length - 1]
        const firstLabel = this.formatDateOnly(first.datetime)
        const lastLabel = this.formatDateOnly(last.datetime)
        return {
          key: `season_${idx + 1}`,
          label: firstLabel === lastLabel ? firstLabel : `${firstLabel} to ${lastLabel}`,
          ceremonyIds: group.map(c => String(c.id)),
        }
      })
    },
    buildPayload(cursor = null) {
      const payload = {
        mode: 'clips',
        universitySlug: this.selectedSlug,
        limit: 50,
        latestPerRegistration: true,
      }
      const query = String(this.searchQuery || '').trim()
      if (query) payload.query = query
      if (this.selectedCeremonyId) {
        payload.ceremonyIds = [this.selectedCeremonyId]
      } else if (this.selectedSeasonKey !== 'all') {
        payload.ceremonyIds = this.ceremoniesForSeason.map(c => String(c.id))
      }
      if (this.statusFilter === 'ready') payload.statuses = ['Generated', 'Notified']
      if (this.statusFilter === 'all') payload.includeAllStatuses = true
      if (cursor) payload.cursor = cursor
      return payload
    },
    async refresh() {
      if (!this.selectedSlug) return
      const requestId = ++this.latestRequestId
      this.loading = true
      this.loadingMore = false
      this.error = ''
      this.items = []
      this.clipMetricsBySubmission = {}
      this.nextCursor = null
      this.hasMore = false
      try {
        const r = await axios.post('/admin/search', this.buildPayload())
        if (requestId !== this.latestRequestId) return
        const rows = Array.isArray(r?.data?.items) ? r.data.items : []
        this.items = rows
        await this.fetchClipMetrics(rows, { reset: true, requestId })
        this.nextCursor = r?.data?.next_cursor || null
        this.hasMore = Boolean(r?.data?.has_more && this.nextCursor)
      } catch (e) {
        if (requestId !== this.latestRequestId) return
        this.error = (e && e.response && e.response.data && e.response.data.error) || 'Failed to load clips'
      } finally {
        if (requestId === this.latestRequestId) this.loading = false
      }
    },
    async loadMore() {
      if (!this.selectedSlug || !this.nextCursor || this.loadingMore) return
      const requestId = ++this.latestRequestId
      this.loadingMore = true
      this.error = ''
      try {
        const r = await axios.post('/admin/search', this.buildPayload(this.nextCursor))
        if (requestId !== this.latestRequestId) return
        const rows = Array.isArray(r?.data?.items) ? r.data.items : []
        await this.fetchClipMetrics(rows, { reset: false, requestId })
        this.items = [...this.items, ...rows]
        this.nextCursor = r?.data?.next_cursor || null
        this.hasMore = Boolean(r?.data?.has_more && this.nextCursor)
      } catch (e) {
        if (requestId !== this.latestRequestId) return
        this.error = (e && e.response && e.response.data && e.response.data.error) || 'Failed to load more clips'
      } finally {
        if (requestId === this.latestRequestId) this.loadingMore = false
      }
    },
    formatDateTime(value) {
      if (!value) return 'Unknown'
      try {
        return new Date(value).toLocaleString()
      } catch (_) {
        return String(value)
      }
    },
    formatDateOnly(value) {
      if (!value) return 'Unknown'
      try {
        return new Date(value).toLocaleDateString()
      } catch (_) {
        return String(value)
      }
    },
    clipMetric(row, metricKey) {
      const submissionId = String(row?.submission_id || '')
      const metrics = this.clipMetricsBySubmission?.[submissionId] || {}
      return Number(metrics?.[metricKey] || 0)
    },
    async fetchClipMetrics(rows, { reset = false, requestId = 0 } = {}) {
      if (!this.showClipMetrics) return
      if (requestId && requestId !== this.latestRequestId) return
      const ids = []
      const seen = new Set()
      for (const row of (rows || [])) {
        const id = String(row?.submission_id || '').trim()
        if (!id || seen.has(id)) continue
        seen.add(id)
        ids.push(id)
      }
      if (!ids.length) {
        if (reset) this.clipMetricsBySubmission = {}
        return
      }
      const base = process.env.VUE_APP_ANALYTICS_API_BASE_URL || ''
      if (!base) {
        this.clipMetricsVisible = false
        this.clipMetricsBySubmission = {}
        return
      }
      const payload = {
        universitySlug: this.selectedSlug,
        mode: 'clip_submission_metrics',
        submissionIds: ids,
      }
      if (this.selectedCeremonyId) {
        payload.ceremonyIds = [this.selectedCeremonyId]
      } else if (this.selectedSeasonKey !== 'all') {
        payload.ceremonyIds = this.ceremoniesForSeason.map(c => String(c.id))
      }
      const token = localStorage.getItem('token') || ''
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      try {
        const r = await axios.post(`${base}/stats/university`, payload, { headers })
        if (requestId && requestId !== this.latestRequestId) return
        const metrics = r?.data?.submission_metrics || {}
        const next = reset ? {} : { ...this.clipMetricsBySubmission }
        for (const id of ids) {
          const row = metrics?.[id] || {}
          next[id] = {
            total_views: Number(row.total_views || 0),
            total_shares: Number(row.total_shares || 0),
          }
        }
        this.clipMetricsBySubmission = next
      } catch (_) {
        this.clipMetricsVisible = false
        this.clipMetricsBySubmission = {}
      }
    },
    formatCeremony(ceremony) {
      if (!ceremony) return 'Unknown ceremony'
      const dt = this.formatDateTime(ceremony.datetime)
      const label = String(ceremony.label || '').trim()
      return label ? `${dt} (${label})` : dt
    },
  },
}
</script>
